import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth'; // Add fetchUserAttributes
import { saveMedicalQuizResults } from '../../../api/medicalQuizApi';
import specialtiesData from './SpecialtyDomains.json'; // Contains each specialty’s ideal scores per domain
import { useNavigate } from 'react-router-dom';
import './MedicalQuizStyles.css';

const MedicalQuizScoring = ({ selectedAnswers }) => {
  const navigate = useNavigate();
  const [recommendedSpecialties, setRecommendedSpecialties] = useState({
    best: [],
    worst: []
  });
  const [matchType, setMatchType] = useState("best");

  // Add state for saving status
  const [saveStatus, setSaveStatus] = useState({
    saving: false,
    error: null,
    success: false
  });

  const [currentUser, setCurrentUser] = useState(null);

  // Add loading state
  const [isInitialized, setIsInitialized] = useState(false);

  // Add loading effect
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Add window size hook
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let progressInterval;
    
    if (isLoading) {
      progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setIsLoading(false);
            return 100;
          }
          return prev + 20;
        });
      }, 200);

      // Ensure we stop loading after max 1.5s
      setTimeout(() => {
        clearInterval(progressInterval);
        setIsLoading(false);
        setLoadingProgress(100);
      }, 1500);
    }

    return () => clearInterval(progressInterval);
  }, [isLoading]);

  function precise(x) {
    return x.toPrecision(2);
  }

  // Map raw domain keys to friendly labels.
  const domainLabels = {
    hospitalCommunity: "Hospital/Community",
    acuteChronic: "Acute/Chronic",
    teamIndividual: "Team/Individual",
    patientContact: "Patient Contact",
    continuityOfCare: "Continuity of Care",
    durationOfTraining: "Training Duration",
    earningPotential: "Earning Potential"
  };

  // Define the order in which domains appear.
  const domainKeys = [
    "hospitalCommunity",
    "acuteChronic",
    "teamIndividual",
    "patientContact",
    "continuityOfCare",
    "durationOfTraining",
    "earningPotential"
  ];

  useEffect(() => {
    calculateRecommendations(selectedAnswers);
    // eslint-disable-next-line
  }, [selectedAnswers]);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        const user = await getCurrentUser();
        if (!user) {
          setSaveStatus({
            saving: false,
            error: 'Please sign in to save your results',
            success: false
          });
          return;
        }
        const attributes = await fetchUserAttributes();
        setCurrentUser({
          ...user,
          attributes,
          userId: attributes.sub
        });
        setIsInitialized(true);
      } catch (error) {
        console.error('Error fetching user:', error);
        setSaveStatus({
          saving: false,
          error: 'Please sign in to save your results',
          success: false
        });
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    const saveResults = async () => {
      if (!currentUser || !recommendedSpecialties.best.length || !recommendedSpecialties.worst.length) {
        return;
      }

      try {
        setSaveStatus({ saving: true, error: null, success: false });
        
        await saveMedicalQuizResults(
          currentUser.userId,
          recommendedSpecialties.best,
          recommendedSpecialties.worst,
          currentUser.username,
          selectedAnswers
        );
        
        setSaveStatus({ saving: false, error: null, success: true });
      } catch (error) {
        setSaveStatus({ 
          saving: false, 
          error: error.message || 'Failed to save results', 
          success: false 
        });
      }
    };

    // Only try to save if we have a user and recommendations
    if (currentUser && recommendedSpecialties.best.length > 0) {
      saveResults();
    }
  }, [currentUser, recommendedSpecialties, selectedAnswers]);

  /**
   * Calculate recommendations by comparing the user's responses
   * to each specialty's ideal domain scores.
   */
  const calculateRecommendations = (answers) => {
    const userDomainScores = mapAnswersToDomains(answers);
    const results = specialtiesData.specialties.map((specialty) => {
      const { domainScores, totalScore } = computePropensityScores(
        userDomainScores,
        specialty.scores
      );
      return {
        name: specialty.name,
        domainScores,            // computed scores for a color-coded breakdown
        totalScore,              // aggregated matching score
        idealScores: specialty.scores // raw ideal values (for the rose chart)
      };
    });

    const numResultsToShow = 7;
    const bestMatches = [...results]
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, numResultsToShow);

    const worstMatches = [...results]
      .sort((a, b) => a.totalScore - b.totalScore)
      .slice(0, numResultsToShow)
      .reverse();

    // NEW: persist Workstyle results locally for use without login
    try {
      localStorage.setItem(
        'medmap.medical.result',
        JSON.stringify({
          bestMatches,
          worstMatches,
          selectedAnswers: answers || []
        })
      );
    } catch (e) {
      console.warn('Failed to cache medical quiz results locally:', e);
    }

    setRecommendedSpecialties({ best: bestMatches, worst: worstMatches });
  };

  /**
   * Convert the array of selected answers into a domain-based object.
   */
  const mapAnswersToDomains = (answers) => {
    return {
      hospitalCommunity: Number(answers[0]) || 0,
      acuteChronic: Number(answers[1]) || 0,
      continuityOfCare: Number(answers[2]) || 0,
      teamIndividual: Number(answers[3]) || 0,
      patientContact: Number(answers[4]) || 0,
      durationOfTraining: Number(answers[5]) || 0,
      earningPotential: Number(answers[6]) || 0
    };
  };

  /**
   * Compute a “propensity score” per domain using a simple “max minus difference” method.
   * The score is normalized to be out of 1 instead of 35.
   */
  const computePropensityScores = (userDomains, specialtyDomains) => {
    const maxScore = 5;
    let domainScores = {};
    let totalScore = 0;
    Object.keys(userDomains).forEach((key) => {
      const userScore = userDomains[key];
      const specialtyScore = specialtyDomains[key];
      const diff = Math.abs(userScore - specialtyScore);
      let score = maxScore - diff;
      if (score < 1) score = 1;
      domainScores[key] = score / 5;  
      
      totalScore += (score / 35) * 100 / 100;
    });
    return { domainScores, totalScore: precise(totalScore) };
  };
  
  /**
   * Convert a computed domain score (1–5) to a stage (for color-coding).
   */
  const getStageForScore = (score) => {
    if (score >= 5) return 5;
    if (score >= 4) return 4;
    if (score >= 3) return 3;
    if (score >= 2) return 2;
    return 1;
  };

  /**
   * Render a colored circle icon based on the domain score.
   */
  const renderScoreIcon = (score) => {
    const stage = getStageForScore(score);
    return (
      <span className={`score-icon score-stage-${stage}`} title={`Score: ${score}`}></span>
    );
  };

  // Pre-calculate the user's raw scores for the rose charts.
  const userScoreValues = mapAnswersToDomains(selectedAnswers);
  const domainNames = domainKeys.map((key) => domainLabels[key] || key);

  // Add responsive layout helper
  const getChartLayout = (isMobile) => ({
    polar: {
      radialaxis: {
        visible: true,
        range: [0, 5],
        tickfont: { size: isMobile ? 8 : 12 }, // Reduced tick font size for mobile
        linewidth: 1,
        gridwidth: 1
      },
      angularaxis: {
        tickfont: { size: isMobile ? 9 : 14 }, // Reduced angular font size for mobile
        linewidth: 1,
        gridwidth: 1,
        // Rotate labels for better mobile fit
        rotation: isMobile ? 0 : undefined,
        direction: 'clockwise'
      }
    },
    margin: {
      t: isMobile ? 30 : 40,  // Reduced top margin
      b: isMobile ? 40 : 60,  // Reduced bottom margin
      l: isMobile ? 25 : 40,  // Significantly reduced left margin for mobile to prevent clipping
      r: isMobile ? 25 : 40,  // Significantly reduced right margin for mobile
      pad: 0
    },
    legend: {
      font: { size: isMobile ? 10 : 14 }, // Smaller legend font
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: isMobile ? -0.15 : -0.1, // Adjusted legend position
      bgcolor: 'rgba(255,255,255,0.9)'
    },
    showlegend: true,
    autosize: true,
    height: isMobile ? 220 : 450, // Reduced height for mobile to fit viewport better
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)'
  });

  /**
   * Render a rose chart comparing the user's responses with the specialty's ideal scores.
   * Enhanced for better visibility and larger display.
   */
  const renderRoseChart = (specialty) => {
    // Shorten domain names for mobile
    const getMobileLabel = (label) => {
      const mobileLabels = {
        'Hospital/Community': 'Hosp/Comm',
        'Acute/Chronic': 'Acute/Chr',
        'Team/Individual': 'Team/Ind',
        'Patient Contact': 'Pt Contact',
        'Continuity of Care': 'Continuity',
        'Training Duration': 'Training',
        'Earning Potential': 'Earnings'
      };
      return isMobile ? mobileLabels[label] || label : label;
    };

    const labels = domainNames.map(getMobileLabel);
    
    const data = [
      {
        type: 'scatterpolar',
        r: domainKeys.map(key => userScoreValues[key] || 0),
        theta: labels,
        fill: 'toself',
        name: 'Your Responses',
        marker: { 
          color: 'rgba(0,123,255,0.7)',
          line: { width: 2, color: 'rgba(0,123,255,1)' }
        },
        line: { width: 2 }
      },
      {
        type: 'scatterpolar',
        r: domainKeys.map(key => specialty.idealScores[key] || 0),
        theta: labels,
        fill: 'toself',
        name: isMobile ? 'Ideal' : `${specialty.name} Ideal`,
        marker: { 
          color: 'rgba(255,99,71,0.7)',
          line: { width: 2, color: 'rgba(255,99,71,1)' }
        },
        line: { width: 2 }
      }
    ];

    return (
      <Plot
        data={data}
        layout={getChartLayout(isMobile)}
        config={{
          responsive: true,
          staticPlot: true,
          displayModeBar: false,
          scrollZoom: false,
          doubleClick: false,
          showTips: false,
          showAxisDragHandles: false
        }}
        className="rose-chart-container"
        style={{
          width: '100%',
          maxWidth: isMobile ? '300px' : '100%', // Constrain max width largely on mobile
          height: isMobile ? '320px' : '450px',
          margin: '0 auto',
          padding: 0
        }}
        useResizeHandler={true}
      />
    );
  };

  /**
   * Render a single specialty card with improved layout
   */
  const renderSpecialtyCard = (specialty) => {
    return (
      <div className="specialty-result" key={specialty.name}>
        <div className="specialty-header">
          <h4>{specialty.name}</h4>
          <span className="total-score">Match: {specialty.totalScore}</span>
        </div>
        <div className="rose-chart-container">
          {renderRoseChart(specialty)}
        </div>
      </div>
    );
  };

  const handleContinueToPathfinder = () => {
    navigate('/dream-specialty');
  };

  // Simplified rendering - removed unnecessary nesting
  const currentSpecialties = recommendedSpecialties[matchType] || [];

  if (isLoading) {
    return (
      <div className="loading-results">
        <h2>Analyzing Your Responses</h2>
        <div className="loading-progress">
          <div 
            className="loading-bar" 
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
        <p>Computing specialty matches...</p>
      </div>
    );
  }

  return (
    <div className="medical-quiz-scoring">
      {saveStatus.saving && <div className="save-status saving">Saving results...</div>}
      {saveStatus.error && <div className="save-status error">{saveStatus.error}</div>}
      {saveStatus.success && <div className="save-status success">Results saved successfully!</div>}
      
      {/* Tab navigation for Best vs. Worst Matches */}
      <div className="match-tabs">
        <button
          className={`match-tab ${matchType === "best" ? "match-tab--active" : ""}`}
          onClick={() => setMatchType("best")}
        >
          <span>Best Matches</span>
        </button>
        <button
          className={`match-tab ${matchType === "worst" ? "match-tab--active" : ""}`}
          onClick={() => setMatchType("worst")}
        >
          <span>Worst Matches</span>
        </button>
      </div>

      {/* Replace view switch with continue button */}
      <div className="view-switch-container">
        <button
          className="view-switch-button"
          onClick={handleContinueToPathfinder}
        >
          Continue to Dream Specialty
        </button>
      </div>

      {/* Rendered directly without extra nesting */}
      {currentSpecialties.map(specialty => renderSpecialtyCard(specialty))}
    </div>
  );
};

export default MedicalQuizScoring;